import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import type { Geometry } from 'geojson';
import { api } from 'lib/request/api';
import type { AreaGeometryPart } from 'types/area-geometry';

/** Wait after the last input change before asking the backend to re-derive. */
const DEBOUNCE_MS = 400;

/** Query-key prefix for the create panel's dry run. */
export const AREA_PREVIEW_QUERY_KEY = 'area-geometries:preview' as const;

interface PreviewResponse {
  geometry: Geometry;
  grossAcres: number;
  parts?: AreaGeometryPart[];
}

/**
 * Everything the preview's dry run needs: the geometry payload, plus the fields a
 * Unit's routed record is validated on.
 */
export interface AreaPreviewInput {
  source: Record<string, unknown>;
  name?: string;
  category?: string;
  metadata?: Record<string, unknown>;
  /**
   * An edit's preview: how the source meets the draft named by
   * `area_geometry_id`. The server composes the two, so the shape it returns is
   * the one the edit saves.
   */
  mode?: 'add' | 'replace';
  area_geometry_id?: number;
}

export interface AreaPreview {
  /** The shape the current inputs would produce, for drawing on the map. */
  geometry: Geometry | null;
  /** Acreage as PostGIS computes it — authoritative, unlike a client estimate. */
  grossAcres: number | null;
  /** Each polygon part's size, as PostGIS measures it. */
  parts: AreaGeometryPart[] | null;
  loading: boolean;
  /**
   * Whether the answer is for the inputs as they are now. While a change waits out
   * the debounce or its call is out, the answer shown is still the last inputs'.
   */
  current: boolean;
  /** The call failed for a reason that says nothing about the inputs (a 500, the network). */
  failed: boolean;
  /** Why the current inputs wouldn't save, as the dry run reports them. */
  errors: string[];
}

const EMPTY: AreaPreview = {
  geometry: null,
  grossAcres: null,
  parts: null,
  loading: false,
  current: true,
  failed: false,
  errors: [],
};

/** A 422's reasons, distinct from a failure that says nothing about the inputs. */
class PreviewRefused extends Error {
  readonly reasons: string[];

  constructor(reasons: string[]) {
    super('Preview refused');
    this.reasons = reasons;
  }
}

/**
 * Turns a failed request into either the dry run's reasons or a plain failure.
 *
 * @param {unknown} error - The thrown ky error.
 * @return {Promise<Error>} A `PreviewRefused` for a 422, else the original.
 */
const toPreviewError = async (error: unknown): Promise<Error> => {
  if (!(error instanceof HTTPError) || error.response.status !== 422) {
    return error instanceof Error ? error : new Error('Preview failed');
  }

  const body = (await error.response.json().catch(() => null)) as {
    errors?: { message?: string }[];
  } | null;

  return new PreviewRefused(
    (body?.errors ?? [])
      .map((entry) => entry.message)
      .filter((message): message is string => !!message)
  );
};

/**
 * Derives the geometry and acreage the current inputs would create, via
 * `POST /area_geometry_previews`, and reports why they wouldn't save — it runs the
 * save path's own dry run, so a clean preview is a shape that will save.
 *
 * @param {AreaPreviewInput | null} input - The preview payload, or null to idle.
 * @return {AreaPreview} The previewed shape, its acreage, any refusals, and
 * whether a call is out.
 */
export const useAreaPreview = (input: AreaPreviewInput | null): AreaPreview => {
  // Serialized so a change of *value* re-derives, not a change of identity. A
  // caller that memoises the payload is spared serializing it on every render,
  // which an uploaded file's shape makes worth sparing.
  const key = useMemo(() => (input ? JSON.stringify(input) : null), [input]);

  // React Query has no debounce of its own, and every keystroke in the panel
  // changes the payload, so the key it queries on trails the inputs.
  const [settledKey, setSettledKey] = useState<string | null>(key);
  useEffect(() => {
    if (key === null) {
      setSettledKey(null);
      return undefined;
    }
    const timer = setTimeout(() => setSettledKey(key), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [key]);

  const { data, isFetching, error } = useQuery<PreviewResponse, Error>({
    queryKey: [AREA_PREVIEW_QUERY_KEY, settledKey],
    queryFn: async ({ signal }) => {
      try {
        return await api
          // Flat, not nested: the endpoint permits `name`/`category` alongside
          // the `source` and `metadata` it reads out of the params.
          .post('area_geometry_previews', {
            json: JSON.parse(settledKey!),
            signal,
          })
          .json<PreviewResponse>();
      } catch (requestError) {
        throw await toPreviewError(requestError);
      }
    },
    enabled: settledKey !== null,
    // A dry run is only true of the inputs it was given, so nothing is kept past
    // the answer, and a refusal must not be retried into a delayed pass.
    gcTime: 0,
    retry: false,
  });

  if (key === null || settledKey === null) {
    return key === null ? EMPTY : { ...EMPTY, current: false };
  }
  const current = settledKey === key && !isFetching;

  // Only a 422 says anything about the inputs. A network failure or a 500 says
  // something about the connection, and reporting that as a reason the area is
  // invalid would both lie and block a save that would work.
  if (error) {
    return {
      ...EMPTY,
      current,
      failed: !(error instanceof PreviewRefused),
      errors: error instanceof PreviewRefused ? error.reasons : [],
    };
  }

  return {
    geometry: data?.geometry ?? null,
    grossAcres: data?.grossAcres ?? null,
    parts: data?.parts ?? null,
    loading: isFetching,
    current,
    failed: false,
    errors: [],
  };
};
